import "./style.css";

import { post, put } from "@selector/shared";
import { TableLoadingOverlay } from "@selector/shared/components";
import { mergeSplitGroups, NAVIGATION_COLUMN_ID, transformFilter, wrapHeaderMenuItems } from "@selector/shared/jsx";
import {
  type components,
  Content,
  SequencingTypeEnum,
  TableRequestIdType,
  TableTypeEnum,
  type TrackEnum,
} from "@selector/shared/openapi";
import type {
  ColDef,
  ColGroupDef,
  ColumnMovedEvent,
  DefaultMenuItem,
  FilterModel,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridColumnsChangedEvent,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  MenuItemDef,
  RowSelectedEvent,
  SelectionChangedEvent,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { isEqual, union, without } from "lodash-es";
import { equals } from "ramda";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HeaderBar, notify, Spacer, themeSelector } from "widgets";
import { useEffectEvent } from "widgets/hooks";
import { getDifferingKeys } from "widgets/utils";

import { fetchFeatureInfo, fetchPangenomeInfo } from "../../api/fetch.ts";
import { getFeatureRowIndex } from "../../api/table/get-feature-row-index.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import type { NavigationFeature } from "../../types.ts";
import {
  getGenomicFeatureTypeFromTrackType,
  getTableTypeFromTrackType,
  getTrackTypeFromTableType,
} from "../../utils/type-conversion.ts";
import { ColumnSelectionPopover } from "./ColumnSelectionPopover.tsx";
import { useTableRestriction } from "./hooks/use-table-restriction.ts";
import { RowCountComponent } from "./RowCountComponent.tsx";
import {
  DEFAULT_COLUMN_DEFINITION,
  DEFAULT_COLUMN_GROUP_DEFINITION,
  MAX_MT_ROW_COUNT,
  SELECTION_COLUMN_DEFINITION,
  TableRestriction,
} from "./settings.ts";
import { TableRestrictionSelect } from "./TableRestrictionSelect.tsx";
import { type TableTypeItem, TableTypeSelect } from "./TableTypeSelect.tsx";
import { RendererCellForNavigation } from "./track-cell/NavigationCell.tsx";
import {
  collectColumnsFromState,
  type ColumnRequest,
  type ColumnSparseResponse,
  type CustomSortModel,
  filterOutFrontendOnlyColumns,
  mergeColumnsWithRemoved,
  toColumnRequests,
} from "./utils";

export const FeatureTable = () => {
  // Ref used to have the correct latest state (when having async data fetching)
  const isTableOriginRef = useRef(false);
  const isLastUnselectAllRef = useRef(false);

  // Ref used to include COLUMN or COUNT in the request payload
  const needsColumnUpdateRef = useRef(true);
  const needsCountUpdateRef = useRef(true);

  const rowCountRef = useRef<number>(undefined);

  const needsToScrollToFeatureAfterTableRefreshRef = useRef(false);
  const needsScrolledRowUpdateRef = useRef(false);
  const latestSelectedFeatureRef = useRef<{
    featureId: number;
    trackType: TrackEnum;
    subTypeId?: number;
  }>(undefined);
  const gridApiRef = useRef<GridApi>(null);
  const valueColsRef = useRef<ColumnRequest[]>([]);
  const templateColumnsRef = useRef<ColumnSparseResponse[]>(null);

  const abortControllerRef = useRef<AbortController>(null);

  const featureSelection = useGenomeViewerStore((state) => state.featureSelection);
  const organismSelection = useGenomeViewerStore((state) => state.organismSelection);
  const latestSelectedFeature = useGenomeViewerStore((state) => state.latestSelectedFeature);
  const syntenyOrganism = useGenomeViewerStore((state) => state.syntenyOrganism);
  const tableRequestBody = useGenomeViewerStore((state) => state.tableRequestBody);
  const tracks = useGenomeViewerStore((state) => state.tracks);
  const trackGroupsInSelection = useGenomeViewerStore((state) => state.trackGroupsInSelection);
  const getFeaturesGroups = useGenomeViewerStore((state) => state.getFeaturesGroups);
  const navigateToFeature = useGenomeViewerStore((state) => state.navigateToFeature);
  const navigateToTrackGroup = useGenomeViewerStore((state) => state.navigateToTrackGroup);
  const postTrackSelectionMessages = useGenomeViewerStore((state) => state.postTrackSelectionMessages);
  const refreshTracks = useGenomeViewerStore((state) => state.refreshTracks);
  const refreshMain = useGenomeViewerStore((state) => state.refreshMain);
  const setTableRequestBody = useGenomeViewerStore((state) => state.setTableRequestBody);

  // Get latest table state (when loading a view)
  const loadedRequestBody = useRef(tableRequestBody);

  const isPangenome = syntenyOrganism.type === SequencingTypeEnum.PANGENOME;
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get("viewid") != null;

  const [rowCount, setRowCount] = useState(0); // We needs to have it as a state to refresh the RowCountComponent
  const [dataUpdated, setDataUpdated] = useState(false);
  const [columnDefs, setColumnDefs] = useState<(ColDef | ColGroupDef)[]>([]);
  const [tableType, setTableType] = useState<TableTypeItem>(
    isPangenome ? { type: TableTypeEnum.PANGENOME } : { type: TableTypeEnum.ORGANISM },
  );

  const visibleTrackGroups = useGenomeViewerStore(
    (state) =>
      [...state.trackGroups.values()]
        .filter(
          (group) =>
            group.graphics.isVisible &&
            // Show all organisms that have a contig visible
            (tableType.type === TableTypeEnum.ORGANISM ||
              // Exclude entities if their track is not visible
              group.trackIds.some(
                (trackId) =>
                  state.tracks.get(trackId)?.isVisible &&
                  state.tracks.get(trackId)?.trackType === getTrackTypeFromTableType(tableType.type),
              )),
        )
        .map((group) => ({ organismId: group.organismId, contigId: group.contigId, groupId: group.groupId })),
    (a, b) => equals(a, b),
  );

  const reqBodyRef = useRef<components["schemas"]["TableRequest"]>(undefined);
  const [initialColumns, setInitialColumns] = useState<ColumnSparseResponse[] | undefined>(undefined);

  const { featureIds, regions, tableRestriction, setTableRestriction } = useTableRestriction(
    tableType,
    visibleTrackGroups,
    isPangenome,
  );

  const baseRequest = {
    idType: TableRequestIdType.ENTITY,
    subTypeId: tableType.subType?.id,
    resultType: tableType.type, // needed for template
    queryType: TableTypeEnum.ORGANISM, // needed for template
    ids: visibleTrackGroups.map(({ organismId }) => organismId), // needed for all columns request as well
    refGenomeId: tableType.type === TableTypeEnum.PANGENOME ? syntenyOrganism.id : undefined,
    valueCols: [],
    endRow: 0,
    startRow: 0,
    sortModel: [],
    filterModel: {},
  } satisfies Partial<components["schemas"]["TableRequest"]>;

  const onFirstDataRendered = ({ api }: FirstDataRenderedEvent) => {
    setTimeout(() => {
      api.sizeColumnsToFit();
    }, 0);
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    // Direct mutation in order not to trigger a re-render with Zustand
    useGenomeViewerStore.getState().gridApiRef.current = params.api;
  };

  const onTableTypeChange = useCallback(
    (tableTypeItem: TableTypeItem) => {
      if (tableType.type === tableTypeItem.type && tableType.subType?.id === tableTypeItem.subType?.id) return;
      abortControllerRef.current?.abort();
      setTableType(tableTypeItem);

      // Refresh the columns
      setColumnDefs([]);
      gridApiRef.current?.resetColumnState(); // Clean the filters, sorting, etc.
      gridApiRef.current?.deselectAll(); // Clean the selection
      templateColumnsRef.current = null;
      valueColsRef.current = [];
      needsColumnUpdateRef.current = true;
      setInitialColumns(undefined);
    },
    [tableType],
  );

  // Navigate to the feature in the Synteny Viewer of the clicked cell
  const onNavigationCellClick = (id: number) => {
    if (!id) return;

    const trackType = getTrackTypeFromTableType(tableType.type);
    if (tableType.type === TableTypeEnum.ORGANISM) {
      navigateToTrackGroup(id);
    } else if (tableType.type === TableTypeEnum.PANGENOME && syntenyOrganism.id) {
      fetchPangenomeInfo(syntenyOrganism.id, id).then(({ start, stop, contigId, orgId }) => {
        if (start !== undefined && stop !== undefined) {
          const item: NavigationFeature = {
            id,
            contigId: contigId,
            orgId: orgId,
            type: undefined,
            subTypeId: tableType.subType?.id,
            start: start,
            stop: stop,
          };
          if (tableRestriction === TableRestriction.REGION) {
            needsCountUpdateRef.current = true;
          }
          navigateToFeature(item);
        }
      });
    } else {
      fetchFeatureInfo(getGenomicFeatureTypeFromTrackType(trackType), id).then(({ start, stop, contigId, orgId }) => {
        if (start !== undefined && stop !== undefined) {
          const item: NavigationFeature = {
            id,
            contigId: contigId,
            orgId: orgId,
            type: getGenomicFeatureTypeFromTrackType(trackType),
            subTypeId: tableType.subType?.id,
            start: start,
            stop: stop,
          };
          if (tableRestriction === TableRestriction.REGION) {
            needsCountUpdateRef.current = true;
          }
          navigateToFeature(item);
        }
      });
    }
  };

  // Update the selection of the zustand store based on the selected rows
  const onRowSelected = ({ source, data, node }: RowSelectedEvent) => {
    if (source !== "checkboxSelected") return;

    const itemId: number | undefined = data?.[NAVIGATION_COLUMN_ID];
    if (!itemId) return;

    if (tableType.type === TableTypeEnum.ORGANISM) {
      useGenomeViewerStore.setState((state) => {
        const { organismSelection } = state;

        let newSelection = [];

        if (node.isSelected()) {
          newSelection = union(organismSelection, [itemId]);
        } else {
          newSelection = without(organismSelection, itemId);
        }

        isTableOriginRef.current = true;

        return { organismSelection: newSelection };
      });
    } else {
      const trackType = getTrackTypeFromTableType(tableType.type);

      const fetchCellInfo =
        tableType.type === TableTypeEnum.PANGENOME && syntenyOrganism.id
          ? fetchPangenomeInfo(syntenyOrganism.id, itemId)
          : fetchFeatureInfo(getGenomicFeatureTypeFromTrackType(trackType), itemId);

      fetchCellInfo.then(({ orgId }) => {
        // Use functional state update so we always merge into the latest state.
        useGenomeViewerStore.setState((state) => {
          const { featureSelection, tracks, trackGroups } = state;

          const selection = new Map(featureSelection);

          const trackIds = [...trackGroups.values()]
            .filter((group) => group.organismId === orgId)
            .flatMap((group) => group.trackIds)
            .filter(
              (trackId) =>
                tracks.get(trackId)?.trackType === trackType &&
                tracks.get(trackId)?.subType?.id === tableType.subType?.id,
            );

          for (const trackId of trackIds) {
            const currentIds = selection.get(trackId) ?? [];
            if (node.isSelected()) {
              selection.set(trackId, union(currentIds, [itemId]));
            } else {
              selection.set(trackId, without(currentIds, itemId));
            }
          }

          isTableOriginRef.current = true;

          return { featureSelection: selection };
        });

        const { trackGroupsInSelection } = useGenomeViewerStore.getState();
        postTrackSelectionMessages(trackGroupsInSelection, undefined);
        refreshTracks();
        refreshMain();
      });
    }
  };

  // Synchronize the "Select all / Unselect all" selection with the Synteny Viewer
  const onSelectionChanged = ({ source, serverSideState }: SelectionChangedEvent) => {
    if (source === "uiSelectAll") {
      // @ts-expect-error Ag-grid fix ? selectAll is not on the type but on the serverSideState object
      if (serverSideState?.selectAll && reqBodyRef.current) {
        isLastUnselectAllRef.current = false;
        post("/table", undefined, undefined, {
          ...reqBodyRef.current,
          startRow: 0, // Needed to get
          endRow: 0, // all the row ids (without restrictions)
          content: [Content.ROW_IDS],
        }).then(({ data: { rowIds } }) => {
          if (rowIds.length >= MAX_MT_ROW_COUNT) {
            notify.warning({
              title: "Too Many Regions Selected",
              subtitle: `Some regions may not be selected as expected`,
              children: `You can select up to ${MAX_MT_ROW_COUNT.toLocaleString("en-US")} regions.`,
            });
          }

          if (!isLastUnselectAllRef.current) {
            if (tableType.type === TableTypeEnum.ORGANISM) {
              useGenomeViewerStore.setState({ organismSelection: rowIds });
            } else {
              const selection = new Map(featureSelection);
              const trackIds = [...tracks.values()]
                .filter(
                  (track) =>
                    track.trackType === getTrackTypeFromTableType(tableType.type) &&
                    track.subType?.id === tableType.subType?.id,
                )
                .map((track) => track.trackId);
              for (const trackId of trackIds) {
                selection.set(trackId, rowIds);
              }
              useGenomeViewerStore.setState({ featureSelection: selection });
              postTrackSelectionMessages(trackGroupsInSelection, undefined);
              refreshTracks();
              refreshMain();
            }
          }
        });
      } else {
        isLastUnselectAllRef.current = true;
        if (tableType.type === TableTypeEnum.ORGANISM) {
          useGenomeViewerStore.setState({ organismSelection: [] });
        } else {
          const selection = new Map(featureSelection);
          const trackIds = [...tracks.values()]
            .filter(
              (track) =>
                track.trackType === getTrackTypeFromTableType(tableType.type) &&
                track.subType?.id === tableType.subType?.id,
            )
            .map((track) => track.trackId);
          for (const trackId of trackIds) {
            selection.delete(trackId);
          }
          useGenomeViewerStore.setState({ featureSelection: selection, selectedRange: undefined });
          postTrackSelectionMessages(trackGroupsInSelection, undefined);
          refreshTracks();
          refreshMain();
        }
      }
    }
  };

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Update the selected rows based on the selection from the zustand store
  useEffect(() => {
    if (isTableOriginRef.current) {
      isTableOriginRef.current = false;
      return;
    }

    // Update based on the organismSelection
    if (tableType.type === TableTypeEnum.ORGANISM) {
      gridApiRef.current?.forEachNode((node) => {
        node.setSelected(organismSelection.includes(node.data?.[NAVIGATION_COLUMN_ID]));
      });
    } else {
      // Update based on the featureSelection
      const featuresMap = getFeaturesGroups(featureSelection);
      const trackType = getTrackTypeFromTableType(tableType.type);

      gridApiRef.current?.forEachNode((node) => {
        const trackFeatureIds = featuresMap.get(trackType)?.flatMap((group) => group.featureIds) ?? [];
        node.setSelected(trackFeatureIds.includes(node.data?.[NAVIGATION_COLUMN_ID]));
      });
    }
  }, [featureSelection, organismSelection, tableType, getFeaturesGroups, dataUpdated]);

  // Loads a saved view? resurrect table correctly
  const restoreTableFromView = useEffectEvent(() => {
    // If syntenyOrganism.id is undefined the table specs will be overriden by the default table specs
    if (loadedRequestBody.current && !!syntenyOrganism.id) {
      // Resurrects the tableRestriction
      const loadedRegion = loadedRequestBody.current.regions;
      const selectedIds = loadedRequestBody.current.selectedIds;
      let newTableRestriction: TableRestriction;

      if (selectedIds != undefined) {
        newTableRestriction = TableRestriction.SELECTION;
      } else if (loadedRegion == undefined) {
        newTableRestriction = TableRestriction.GENOME;
      } else if (loadedRegion.some((reg) => reg.range != undefined)) {
        newTableRestriction = TableRestriction.REGION;
      } else {
        newTableRestriction = TableRestriction.CONTIG;
      }

      setTableRestriction(newTableRestriction);

      const type = loadedRequestBody.current.resultType;

      const subType = [...tracks.values()].find(
        (track) =>
          track.trackType === getTrackTypeFromTableType(type) &&
          track.subType?.id === loadedRequestBody.current?.subTypeId,
      )?.subType;
      // Resurrects the tableType
      setTableType({ type, subType });

      valueColsRef.current = (loadedRequestBody.current.valueCols ?? []) as ColumnRequest[];
    }
  });

  const scrollToRowIndex = (rowIndex: number) => {
    if (!gridApiRef.current) {
      return;
    }
    const firstVisibleRowIndex = gridApiRef.current.getFirstDisplayedRowIndex();
    const lastVisibleRowIndex = gridApiRef.current.getLastDisplayedRowIndex();
    const isVisible = rowIndex >= firstVisibleRowIndex && rowIndex <= lastVisibleRowIndex; // Visible in the viewport, i.e renderer rows (changing rowBuffer can break this)

    if (!isVisible) {
      gridApiRef.current.ensureIndexVisible(rowIndex, "top");
    }
  };

  useEffect(() => {
    restoreTableFromView();
  }, [tableRequestBody, syntenyOrganism]);

  useEffect(() => {
    needsScrolledRowUpdateRef.current = !!latestSelectedFeature;
    latestSelectedFeatureRef.current = latestSelectedFeature;
  }, [latestSelectedFeature]);

  // When selecting a feature in the Synteny Viewer, scroll to its row, if possible
  useEffect(() => {
    if (
      !reqBodyRef.current ||
      latestSelectedFeatureRef.current == undefined ||
      !needsScrolledRowUpdateRef.current ||
      tableRestriction === TableRestriction.SELECTION ||
      !latestSelectedFeature
    ) {
      return;
    }
    const { featureId, trackType, subTypeId } = latestSelectedFeature;
    const featureTableType = getTableTypeFromTrackType(trackType);
    const featureTableSubType = [...tracks.values()].find(
      (track) => track.trackType === trackType && track.subType?.id === subTypeId,
    )?.subType;

    // If the trackType of the selected feature is already matching the table type
    if (featureTableType === tableType.type && subTypeId === tableType.subType?.id) {
      // Prevent to request twice the row index when the tableType of the LOADED table is not matching the feature type
      if (needsToScrollToFeatureAfterTableRefreshRef.current) {
        return;
      }
      gridApiRef.current?.setGridOption("loading", true);
      getFeatureRowIndex(reqBodyRef.current, featureId)
        .then(({ rowIndex }) => {
          if (rowIndex >= 0) {
            scrollToRowIndex(rowIndex);
          }
        })
        .catch(() => {
          notify.error({
            title: "Table limit exceeded",
            children: "Apply additional filters to reduce the number of items in order to find the feature.",
          });
        })
        .finally(() => {
          gridApiRef.current?.setGridOption("loading", false);
        });
      needsScrolledRowUpdateRef.current = false;
    }
    // Else refresh the table and once data is fetched (using the needsToScrollToFeatureAfterTableRefreshRef) scroll to index
    else {
      onTableTypeChange({
        type: featureTableType,
        subType: featureTableSubType,
      });
      needsToScrollToFeatureAfterTableRefreshRef.current = true;
    }
  }, [tableType, tableRestriction, latestSelectedFeature, tracks, onTableTypeChange]);

  const datasource: IServerSideDatasource = {
    getRows({ api, request, fail, success }: IServerSideGetRowsParams) {
      // Only show the loading overlay when columns changes
      if (needsColumnUpdateRef.current) {
        api.setGridOption("loading", true);
      }

      if (syntenyOrganism.id == undefined) {
        return;
      }

      const requestValueCols =
        valueColsRef.current.length > 0
          ? valueColsRef.current
          : ((loadedRequestBody.current?.valueCols as ColumnRequest[] | undefined) ?? []);

      // Loads a saved view? get correct data from MT
      const requestBody = loadedRequestBody.current
        ? {
            ...loadedRequestBody.current,
            content: [Content.ROWS, Content.COLUMNS],
            refGenomeId: loadedRequestBody.current.refGenomeId ?? undefined,
            regions: loadedRequestBody.current.resultType === TableTypeEnum.ORGANISM ? undefined : regions,
            selectedIds: loadedRequestBody.current.selectedIds ?? undefined,
            subTypeId: loadedRequestBody.current.subTypeId ?? undefined,
            startRow: request.startRow ?? 0,
            endRow: request.endRow ?? 0,
            valueCols: requestValueCols,
          }
        : {
            ...baseRequest,
            content: [Content.ROWS].concat(needsColumnUpdateRef.current ? [Content.COLUMNS] : []),
            // Since we keep the latest tableRestriction when we switch to the Organism table (but we hide the selection menu in this case), ignore the selected featureIds
            selectedIds:
              tableType.type !== TableTypeEnum.ORGANISM && tableRestriction === TableRestriction.SELECTION
                ? featureIds
                : undefined,
            regions: tableType.type === TableTypeEnum.ORGANISM ? undefined : regions,
            startRow: request.startRow ?? 0,
            endRow: request.endRow ?? 0,
            sortModel: request.sortModel as CustomSortModel,
            filterModel: transformFilter(request.filterModel) as FilterModel,
            valueCols: requestValueCols,
          };

      // For saving views
      if (loadedRequestBody.current === undefined) {
        setTableRequestBody(requestBody);
      }

      // This logic is required not to request filteredCount at each update (that is expensive in the MT)
      if (reqBodyRef.current) {
        const changingCountKeys = new Set(["subTypeId", "resultType", "ids", "regions", "selectedIds", "filterModel"]);
        const changedKeys = getDifferingKeys(requestBody, reqBodyRef.current);
        if (changedKeys.some((key) => changingCountKeys.has(key))) {
          needsCountUpdateRef.current = true;
        }
      }

      reqBodyRef.current = requestBody;
      const controller = new AbortController();
      abortControllerRef.current = controller;

      post(
        "/table",
        undefined,
        undefined,
        {
          ...requestBody,
          content: requestBody.content.concat(needsCountUpdateRef.current ? [Content.COUNTS] : []),
        },
        "json",
        controller.signal,
      )
        .then(({ data: { columns, data, filteredCount } }) => {
          if (!isViewMode) {
            valueColsRef.current = [];
          }

          if (columns.length > 0) {
            const sanitizedColumns = filterOutFrontendOnlyColumns(columns);
            const { colDefs, expandedLeafOrder } = mergeSplitGroups(
              sanitizedColumns,
              loadedRequestBody.current?.sortModel,
              { extraMainMenuItems: wrapHeaderMenuItems },
            );

            const extendedColDef = colDefs.map((colDef) =>
              colDef.colId === NAVIGATION_COLUMN_ID
                ? {
                    ...colDef,
                    cellRenderer: RendererCellForNavigation({ onNavigationCellClick }), // Synteny Viewer specific cell
                  }
                : colDef,
            );

            gridApiRef.current?.setGridOption("columnDefs", extendedColDef); // Required to apply correctly the filters when loading a saved view
            setColumnDefs(extendedColDef);
            setInitialColumns(sanitizedColumns);

            // Restore the correct column order
            queueMicrotask(() => {
              api?.applyColumnState({
                state: [{ colId: "ag-Grid-SelectionColumn" }].concat(expandedLeafOrder.map((colId) => ({ colId }))),
                applyOrder: true,
              });
            });

            // Loads a saved view? resurrect filter model
            if (loadedRequestBody.current) {
              api.setFilterModel(loadedRequestBody.current.filterModel);
            }
          }
          if (data.length > 0) {
            api.hideOverlay();
          } else {
            api.showNoRowsOverlay();
          }

          if (filteredCount != undefined) {
            rowCountRef.current = Math.min(filteredCount, MAX_MT_ROW_COUNT);
            setRowCount(rowCountRef.current);
          }

          success({ rowData: data, rowCount: rowCountRef.current });

          if (
            latestSelectedFeatureRef.current &&
            ((tableRestriction === TableRestriction.SELECTION &&
              needsScrolledRowUpdateRef.current &&
              requestBody.selectedIds?.includes(latestSelectedFeatureRef.current.featureId)) ||
              needsToScrollToFeatureAfterTableRefreshRef.current)
          ) {
            needsScrolledRowUpdateRef.current = false;
            getFeatureRowIndex(requestBody, latestSelectedFeatureRef.current.featureId)
              .then(({ rowIndex }) => {
                if (rowIndex >= 0) {
                  scrollToRowIndex(rowIndex);
                }
              })
              .catch(() => {
                notify.error({
                  title: "Table limit exceeded",
                  children: "Apply additional filters to reduce the number of items in order to find the feature.",
                });
              });
          }
        })
        .catch((error) => {
          if ((error as DOMException)?.name === "AbortError") {
            return;
          }

          if (import.meta.env.DEV) {
            console.error("Table fail: ", error); // Kept for dev until the tables are done
          }
          fail();
        })
        .finally(() => {
          if (controller.signal.aborted) {
            return;
          }
          if (needsColumnUpdateRef.current) {
            api.setGridOption("loading", false);
            setTimeout(() => {
              api.sizeColumnsToFit();
            }, 30);
          }

          if (needsToScrollToFeatureAfterTableRefreshRef.current) {
            needsToScrollToFeatureAfterTableRefreshRef.current = false;
          }

          needsColumnUpdateRef.current = false;
          needsCountUpdateRef.current = false;
          setDataUpdated((prevDataUpdated) => !prevDataUpdated);
          if (!!syntenyOrganism.id && !!loadedRequestBody.current) {
            loadedRequestBody.current = undefined;
          }
        });
    },
  };

  const tableSpecForTemplates = {
    // Please leave it like this. This is the minimal required fields we need for templates
    idType: baseRequest.idType,
    subTypeId: baseRequest.subTypeId,
    resultType: baseRequest.resultType,
    queryType: baseRequest.queryType,
    ids: baseRequest.ids,
    valueCols: baseRequest.valueCols,
    sortModel: baseRequest.sortModel,
    filterModel: baseRequest.filterModel,
    refGenomeId: baseRequest.refGenomeId,
  } satisfies Partial<components["schemas"]["TableRequest"]>;

  const handleColumnMove = (event: ColumnMovedEvent | GridColumnsChangedEvent) => {
    const columnState = event.api.getColumnState();
    const columnDefs = event.api.getColumnDefs();
    const currentColumns = filterOutFrontendOnlyColumns(collectColumnsFromState(columnState, columnDefs));
    const previousColumns = templateColumnsRef.current ? filterOutFrontendOnlyColumns(templateColumnsRef.current) : [];

    const columns = mergeColumnsWithRemoved(currentColumns, previousColumns);

    if (columns.length === 0) {
      templateColumnsRef.current = null;
      return;
    }

    const hasSameOrderAndVisibility = isEqual(
      previousColumns.map((column) => ({
        colId: column.colId,
        hide: Boolean(column.hide),
        fixed: Boolean(column.fixed),
      })),
      columns.map((column) => ({
        colId: column.colId,
        hide: Boolean(column.hide),
        fixed: Boolean(column.fixed),
      })),
    );

    templateColumnsRef.current = columns;
    setInitialColumns(columns);

    if (isViewMode) {
      const selectedValueCols = toColumnRequests(columns);
      valueColsRef.current = selectedValueCols;

      if (loadedRequestBody.current) {
        setTableRequestBody(loadedRequestBody.current);
      }

      return;
    }

    valueColsRef.current = [];

    if (hasSameOrderAndVisibility) {
      return;
    }

    put("/template", undefined, undefined, {
      columns,
      resultType: tableSpecForTemplates.resultType,
      queryType: tableSpecForTemplates.queryType,
    });
  };

  const getContextMenuItems = (params: GetContextMenuItemsParams) => {
    const result: (DefaultMenuItem | MenuItemDef)[] = [
      {
        name: "Navigate",
        icon: `<div class="context-menu-icon-container"><span class="material-symbols-rounded context-menu-icon context-menu-icon-active">arrow_forward</span></div>`,
        action: () => {
          onNavigationCellClick(params.node?.data?.[NAVIGATION_COLUMN_ID]);
        },
        cssClasses: ["context-menu-main-action"],
      },
      "separator",
      "cut",
      "copy",
      "copyWithHeaders",
      "paste",
      "export",
    ];
    return result;
  };

  return (
    <>
      <HeaderBar
        actions={[
          <TableTypeSelect key={"table-type-select"} tableType={tableType} onChange={onTableTypeChange} />,
          // Hide the table restriction Select for Organism table (since the only available option is Entire Genome)
          tableType.type !== TableTypeEnum.ORGANISM && (
            <TableRestrictionSelect
              key={"table-restriction-select"}
              tableType={tableType}
              tableRestriction={tableRestriction}
              visibleTrackGroups={visibleTrackGroups}
              onChange={(restriction) => {
                setTableRestriction(restriction);
              }}
            />
          ),
          <RowCountComponent key={"row-count"} rowCount={rowCount} />,
          <Spacer key={"spacer"} />,
          <ColumnSelectionPopover
            key={"column-selection"}
            baseRequest={baseRequest}
            tableSpecForTemplates={tableSpecForTemplates}
            isViewMode={isViewMode}
            initialValueColumns={initialColumns}
            onApply={(columns) => {
              const sanitizedColumns = filterOutFrontendOnlyColumns(columns);

              templateColumnsRef.current = sanitizedColumns.length > 0 ? sanitizedColumns : null;
              setInitialColumns(sanitizedColumns);

              if (isViewMode) {
                valueColsRef.current = toColumnRequests(sanitizedColumns);

                if (loadedRequestBody.current) {
                  setTableRequestBody({ ...loadedRequestBody.current, valueCols: valueColsRef.current });
                }
              } else {
                valueColsRef.current = [];
              }

              needsColumnUpdateRef.current = true;

              // Refresh the server-side datasource to fetch new columns
              gridApiRef.current?.refreshServerSide({ purge: true });
            }}
          />,
        ]}
        style={{ paddingLeft: 10, paddingRight: 10 }}
      />
      <AgGridReact
        // Callbacks
        onFirstDataRendered={onFirstDataRendered}
        onGridReady={onGridReady}
        onRowSelected={onRowSelected}
        onSelectionChanged={onSelectionChanged}
        // Server options
        rowModelType="serverSide"
        cacheBlockSize={50}
        maxBlocksInCache={5}
        serverSideDatasource={datasource}
        rowBuffer={0} // Required to prevent scrolling the table to an already visible rowIndex (see scrollToRowIndex)
        suppressRowTransform={true} // Improves performances
        suppressServerSideFullWidthLoadingRow={true}
        // Columns/rows
        selectionColumnDef={SELECTION_COLUMN_DEFINITION}
        defaultColDef={DEFAULT_COLUMN_DEFINITION}
        defaultColGroupDef={DEFAULT_COLUMN_GROUP_DEFINITION}
        columnDefs={columnDefs}
        rowSelection={{ mode: "multiRow", copySelectedRows: true, enableClickSelection: false, checkboxes: true }}
        cellSelection={true}
        suppressDragLeaveHidesColumns={true}
        // Styling
        autoSizeStrategy={{ type: "fitCellContents" }}
        tooltipShowMode="whenTruncated"
        tooltipShowDelay={500}
        enableBrowserTooltips={false}
        popupParent={document.querySelector("body")}
        theme={themeSelector}
        rowHeight={27}
        loadingOverlayComponent={TableLoadingOverlay}
        overlayNoRowsTemplate="No rows to display"
        onColumnMoved={(event) => {
          // we should not react to changes made via the api here
          if (event.source === "uiColumnMoved" && event.finished) {
            handleColumnMove(event);
          }
        }}
        getMainMenuItems={() => []} // Required to set empty array as default to prevent right click on empty areas in the header
        getContextMenuItems={getContextMenuItems}
        enableFilterHandlers
      />
    </>
  );
};
